export type MediaSourceId = 'library' | 'storyblocks';
export type MediaKind = 'image' | 'video';

export interface ArtifactRef {
  artifactId: string | null;
  source: MediaSourceId;
  libraryMediaFileId: string | null;
  storyBlocksVideoId: number | null;
  thumbnailUrl: string;
  previewUrl: string;
  fileDuration: number;
  kind: MediaKind;
  startTime: number;
  endTime: number;
  isNotEditable?: boolean;
}

export interface Slot {
  slotKey: string;
  original: ArtifactRef | null;
  current: ArtifactRef | null;
}

export type SlotStatus =
  | 'unchanged'
  | 'replaced'
  | 'added'
  | 'deleted'
  | 'empty';

export interface MediaPickerResult {
  source: MediaSourceId;
  libraryMediaFileId: string | null;
  storyBlocksVideoId: number | null;
  thumbnailUrl: string;
  previewUrl: string;
  fileDuration: number;
  kind: MediaKind;
}

export interface SaveArtifact {
  artifactId: string | null;
  libraryMediaFileId: string | null;
  storyBlocksVideoId: number | null;
  order: number;
  startTime: number;
  duration: number;
}

export const slotStatus = (slot: Slot): SlotStatus => {
  if (!slot.original && !slot.current) return 'empty';
  if (!slot.original && slot.current) return 'added';
  if (slot.original && !slot.current) return 'deleted';
  return artifactsEqual(slot.original, slot.current) ? 'unchanged' : 'replaced';
};

export const artifactsEqual = (
  a: ArtifactRef | null,
  b: ArtifactRef | null,
): boolean => {
  if (!a || !b) return a === b;
  return (
    a.source === b.source &&
    a.libraryMediaFileId === b.libraryMediaFileId &&
    a.storyBlocksVideoId === b.storyBlocksVideoId
  );
};

export type SlotBadge = 'new' | 'replaced' | 'edited' | 'deleted' | 'reordered';

export const slotBadgeOf = (
  slot: Slot,
  isReordered = false,
): SlotBadge | null => {
  if (!slot.original && !slot.current) return null;
  if (!slot.original && slot.current) return 'new';
  if (slot.original && !slot.current) return 'deleted';
  if (!slot.original || !slot.current) return null;
  if (!artifactsEqual(slot.original, slot.current)) return 'replaced';
  if (
    slot.original.startTime !== slot.current.startTime ||
    slot.original.endTime !== slot.current.endTime
  )
    return 'edited';
  if (isReordered) return 'reordered';
  return null;
};

export const slotDurationOf = (slot: Slot) =>
  slot.current ? Math.max(0, slot.current.endTime - slot.current.startTime) : 0;

export const isSlotLocked = (slot: Slot) =>
  !!(slot.current?.isNotEditable || slot.original?.isNotEditable);

export const usedDurationOf = (slots: Slot[]) =>
  slots.reduce((sum, s) => sum + slotDurationOf(s), 0);

export const isImageUrl = (url: string) => {
  const lower = url.toLowerCase().split('?')[0];
  return (
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.png') ||
    lower.endsWith('.gif') ||
    lower.endsWith('.webp')
  );
};
